import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import { Add, LibraryBooks, Biotech } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { equipmentAPI } from '../../../equipment/services/equipment';
import { tasksAPI } from '../../../tasks/services/tasks';
import { taskEquipmentAPI } from '../../../tasks/services/taskEquipment';
import LoadErrorAlert from '../LoadErrorAlert';

// Where task creation returns to when done (step index is 0-based)
const RETURN_TO = '/onboarding?step=4';

export default function Step5Tasks() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [equipment, setEquipment] = useState([]);
  const [tasksByEquipment, setTasksByEquipment] = useState({});
  const [generalTasks, setGeneralTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [equipRes, tasksRes, linksRes] = await Promise.all([
        equipmentAPI.listEquipmentByDepartment(),
        tasksAPI.getDepartmentTasks({ limit: 1000 }),
        taskEquipmentAPI.getDepartmentTaskEquipmentLinks(),
      ]);
      const equipList = equipRes.data?.equipment || [];
      // Only the first task of a recurring chain, so each task is listed once
      const taskList = (tasksRes.data?.data?.tasks || []).filter(task => !task.parent_task_id);
      const links = linksRes.data?.data?.relationships || [];
      setEquipment(equipList);

      const taskById = {};
      taskList.forEach(task => { taskById[task.task_id] = task; });

      const map = {};
      links.forEach(rel => {
        const eid = rel.equipment?.equipment_id;
        const task = taskById[rel.task_id];
        if (eid && task) {
          if (!map[eid]) map[eid] = [];
          map[eid].push(task);
        }
      });
      setTasksByEquipment(map);

      const linkedTaskIds = new Set(links.map(rel => rel.task_id));
      setGeneralTasks(taskList.filter(task => !linkedTaskIds.has(task.task_id)));
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createTaskForEquipment = (item) =>
    navigate('/tasks/create', { state: { returnTo: RETURN_TO, prefillEquipment: [item] } });

  const renderTaskRow = (task) => (
    <Stack key={task.task_id} direction="row" alignItems="center" spacing={1}>
      <Typography variant="body2">{task.title}</Typography>
      {task.priority && (
        <Chip
          label={t(`tasks:priority.${task.priority.toLowerCase()}`)}
          size="small"
          variant="outlined"
          sx={{ fontSize: 10, height: 20 }}
        />
      )}
    </Stack>
  );

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        {t('onboarding:step5.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('onboarding:step5.subtitle')}
      </Typography>

      <Stack direction="row" spacing={1} mb={3}>
        <Button
          variant="contained"
          startIcon={<LibraryBooks />}
          onClick={() => navigate('/tasks/catalog', { state: { returnTo: RETURN_TO } })}
          size="small"
        >
          {t('onboarding:step5.browse_templates')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={() => navigate('/tasks/create', { state: { returnTo: RETURN_TO } })}
          size="small"
        >
          {t('onboarding:step5.create_task')}
        </Button>
      </Stack>

      {loading ? (
        <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
      ) : loadError ? (
        <LoadErrorAlert onRetry={fetchData} />
      ) : (
        <>
          {equipment.length === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {t('onboarding:step5.no_equipment')}
            </Alert>
          )}

          {equipment.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary" mb={1}>
                {t('onboarding:step5.equipment_needing_tasks')}
              </Typography>
              <Stack spacing={1}>
                {equipment.map(e => {
                  const equipTasks = tasksByEquipment[e.equipment_id] || [];
                  return (
                    <Card key={e.equipment_id} variant="outlined">
                      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between">
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Biotech color="action" fontSize="small" />
                            <Typography variant="body2" fontWeight={600}>{e.name}</Typography>
                            {e.equipment_type && (
                              <Chip
                                label={t(`equipment:equipment_types.${e.equipment_type}`, { defaultValue: e.equipment_type })}
                                size="small"
                                variant="outlined"
                                sx={{ fontSize: 10, height: 20 }}
                              />
                            )}
                          </Stack>
                          <Button
                            size="small"
                            startIcon={<Add />}
                            onClick={() => createTaskForEquipment(e)}
                          >
                            {t('onboarding:step5.create_task')}
                          </Button>
                        </Stack>
                        {equipTasks.length > 0 && (
                          <>
                            <Divider sx={{ my: 1 }} />
                            <Stack spacing={0.5} pl={3}>
                              {equipTasks.map(renderTaskRow)}
                            </Stack>
                          </>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </>
          )}

          <Typography variant="subtitle2" color="text.secondary" mt={3} mb={1}>
            {t('onboarding:step5.general_tasks')}
          </Typography>
          {generalTasks.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('onboarding:step5.no_general_tasks')}
            </Typography>
          ) : (
            <Card variant="outlined">
              <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                <Stack spacing={0.5}>
                  {generalTasks.map(renderTaskRow)}
                </Stack>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </Box>
  );
}
